import { Title } from "components";
import { AgGrid, AgGridRef, GridState, RowGroupTree, RowGroupInfo, ZColDef, RowGroupNode, RowGroupLeaf } from "components/AgGrid/AgGrid";
import { Row, Col, Card, Tabs, Tab, Button, Dropdown, OverlayTrigger, Tooltip, Spinner } from "react-bootstrap";
import { Link } from "react-router-dom";
import { RootState } from "redux/store";
import { useAppDispatch, useAppSelector } from 'redux/hooks';
import 'ag-grid-enterprise';
import { useEffect, useRef, useState } from "react";
import { generateGroupTillReport, exportTillReport, updateReportField, cleanReports } from "redux/slices/Reports";
import { ZMultiSelectDropdown } from "components/ZMultiSelectDropdown";
import { ZDateTimePicker } from "components/ZDateTimePicker";
import { add, formatISO, set } from "date-fns";
import { ZCheckboxButton } from "components/ZCheckboxButton";
import { ZSingleSelectDropdown } from "components/ZSingleSelectDropdown";
import { TimedMessage } from "components/TimedMessage";
import { customSum } from "helpers/utility/aggrid/customAggFuncs";
import { ZIcon } from "components/ZIcon";
import { ZIconType } from "helpers/utility/iconMap";
import { ZTooltip } from "components/ZTooltip";
import { TillReportPeriodData, TillPeriodGroupingIntervals, TillReportFunction } from "models/functionModels/reportFunctions/TillReportFunction";
import { ReportExportBody } from "models/common/storeGroupMessag";
import { NonEmptyArray } from "helpers/utility/types";



type GroupInfo = {
	isField?: boolean;
	value: string;
}

type RowInfo = {
	mainGroup?: GroupInfo,
	subGroup?: GroupInfo,
	subSubGroup?: GroupInfo,
	subSubSubGroup?: GroupInfo,
	leafValue: GroupInfo,
	isValueNegative?: boolean,
	key: keyof TillReportPeriodData | null
}

export function GroupTillReport() {

	const report = useAppSelector((state: RootState) => state.reports);
	const stocks = useAppSelector((state: RootState) => state.stocks);
	const stores = useAppSelector((state: RootState) => state.stores);
	const dispatch = useAppDispatch();

	const [generating, setGenerating] = useState(false);
	const [constructing, setConstructing] = useState(false);
	const [exporting, setExporting] = useState(false);

	const [startDate, setStartDate] = useState<Date | null>(getDateNow());
	const [startDateError, setStartDateError] = useState<string | null>();
	const [endDate, setEndDate] = useState<Date | null>(getDateNow());
	const [endDateError, setEndDateError] = useState<string | null>();
	const [selectedStores, setSelectedStores] = useState<any[] | undefined>(undefined);

	useEffect(() => {
		setInterval(TillPeriodGroupingIntervals.Daily);
		dispatch(cleanReports());
	}, []);

	useEffect(() => {
		constructTillSummaryTable();
	}, [report.table]);

	function handleSubmit(g?: GridState) {
	}

	const [completeValueTable, setCompleteValueTable] = useState<any[]>([]);
	const [completeCountTable, setCompleteCountTable] = useState<any[]>([]);
	const completeTableRef = useRef<AgGridRef | null>(null);

	const [tillSummaryValueTable, setTillSummaryValueTable] = useState<any[]>([]);
	const [tillSummaryCountTable, setTillSummaryCountTable] = useState<any[]>([]);

	const [gstSummaryValueTable, setGstSummaryValueTable] = useState<any[]>([]);
	const [gstSummaryCountTable, setGstSummaryCountTable] = useState<any[]>([]);

	const [totalTakingsValueTable, setTotalTakingsValueTable] = useState<any[]>([]);
	const [totalTakingsCountTable, setTotalTakingsCountTable] = useState<any[]>([]);

	const [paymentBreakdownValueTable, setPaymentBreakdownValueTable] = useState<any[]>([]);
	const [paymentBreakdownCountTable, setPaymentBreakdownCountTable] = useState<any[]>([]);
	
	const [statsSummaryValueTable, setStatsSummaryValueTable] = useState<any[]>([]);
	const [statsSummaryCountTable, setStatsSummaryCountTable] = useState<any[]>([]);
	
	const [showCount, setShowCount] = useState<boolean>(false);
	
	const [tableColDefs, setTableColDefs] = useState<ZColDef[]>(
		[
			{
				headerName: '',
				pinned: true,
				colId: 'mainGroupGroup',
				minWidth: 220,
				showRowGroup: true,
				cellRenderer: 'agGroupCellRenderer',
				valueGetter: (params: any) => {
					return params.data?.leafValue ? params.data.leafValue : null;
				},
				cellRendererParams: {
					// turn off the row count
					suppressCount: true,
				},
			},
			{ field: "tabGroup", rowGroup: true, hide: true },
			{ field: "mainGroup", rowGroup: true, hide: true },
			{ field: "subGroup", rowGroup: true, hide: true, cellRendererSelector: (params) => {
				if (params.node.key === "Prescription Sales (inc Government Contribution)") {
					return { 
						component: 'baseCellRenderer',
						params: { 
							decorator:
								<ZTooltip enabled text={"This value isn't included in the Total Sales aggregation"}><ZIcon icon={ZIconType.Information} size="14px" /></ZTooltip>, 
							decoratorPosition: "end"
						}
					}
				}
			}  },
			{ field: "subSubGroup", rowGroup: true, hide: true },
			{ field: "subSubSubGroup", rowGroup: true, hide: true },
			{ field: "leafValue", hide: true },
		]);

	const [periodColValueDefs, setPeriodColValueDefs] = useState<ZColDef[]>([]);
	const [periodColCountDefs, setPeriodColCountDefs] = useState<ZColDef[]>([]);

	const [generateButtonEnabled, setGenerateButtonEnabled] = useState<boolean>(false);
	const [generateButtonTooltip, setGenerateButtonTooltip] = useState<string>("");
	const [exportButtonEnabled, setExportButtonEnabled] = useState<boolean>(false);
	const [exportButtonTooltip, setExportButtonTooltip] = useState<string>("");
	const [selectedInterval, setSelectedInterval] = useState<TillPeriodGroupingIntervals>();

	enum DateRange {
		FinancialYear = "Financial Year",
		CalendarYear = "Calendar Year",
		Week = "Week",
		Month = "Month"
	}

	const [dateRangeOptions, setDateRangeOptions] = useState<DateRange[]>([]);
	const [selectedDateRange, setSelectedDateRange] = useState<DateRange | undefined>(undefined);

	function getDateNow(){
		//Get the current date without seconds
		const now = new Date();
		now.setSeconds(0);
		now.setMilliseconds(0);

		return now;
	}

	useEffect(() => {
		if (!selectedStores || selectedStores.length === 0) {
			setGenerateButtonEnabled(false);
			setExportButtonEnabled(false);
			setGenerateButtonTooltip("Please select at least one store to generate report for");
			setExportButtonTooltip("Please select at least one store to export report for");
		} else if (startDateError || endDateError) {
			setGenerateButtonEnabled(false);
			setExportButtonEnabled(false);
			setGenerateButtonTooltip("Please select a valid start and end date to generate report for");
			setExportButtonTooltip("Please select a valid start and end date to export report for");
		} else {
			setGenerateButtonEnabled(true);
			setExportButtonEnabled(true);
			setGenerateButtonTooltip("");
			setExportButtonTooltip("");
		}

		if (report.table.length === 0) {
			setExportButtonEnabled(false);
			setExportButtonTooltip("A report must be generated before you can export");
		}
	}, [selectedStores, startDateError, endDateError, report.table]);

	useEffect(() => {
		setEndDate(getDateNow());
		onStartDateChanged(getDateNow());
	}, [selectedDateRange]);

	function setInterval(interval?: TillPeriodGroupingIntervals) {
		setSelectedInterval(interval);
		switch(interval) {
			case TillPeriodGroupingIntervals.Daily:
				setDateRangeOptions([DateRange.Week, DateRange.Month]);
				setSelectedDateRange(DateRange.Week);
				break;
			case TillPeriodGroupingIntervals.Monthly:
				setDateRangeOptions([DateRange.FinancialYear, DateRange.CalendarYear]);
				setSelectedDateRange(DateRange.FinancialYear);
				break;
			default:
				setDateRangeOptions([]);
				setSelectedDateRange(undefined);
				break;
		}
	}

	function onStartDateChanged(d: Date | null) {
		selectedInterval === TillPeriodGroupingIntervals.Custom ? setStartDate(d) : d && setStartAndEndDate(d);
	}

	function setStartAndEndDate(startDate: Date) {
		let newStartDate = startDate;
		let newEndDate = endDate;
		switch(selectedInterval) {
			case TillPeriodGroupingIntervals.Monthly:
				switch(selectedDateRange) {
					case DateRange.FinancialYear:
						newStartDate = set(startDate, {month: 6, date: 1});
						newEndDate = add(newStartDate, {years: 1, days: -1});
						break;
					case DateRange.CalendarYear:
						newStartDate = set(startDate, {month: 0, date: 1});
						newEndDate = add(newStartDate, {years: 1, days: -1});
						break;
					default:
						break;
				}
				break;
			case TillPeriodGroupingIntervals.Daily:
				switch(selectedDateRange) {
					case DateRange.Week:
						newEndDate = add(startDate, {days: 6});
						break;
					case DateRange.Month:
						newStartDate = set(startDate, {date: 1});
						newEndDate = add(newStartDate, {months: 1, days: -1});
						break;
					default:
						break;
				}
				break;
			default:
				break;
		}

		// * Set start time to midnight, set end time to 11:59:59PM.
		newStartDate = set(newStartDate, {hours: 0, minutes: 0, seconds: 0});
		newEndDate = newEndDate && add(set(newEndDate, {hours: 0, minutes: 0, seconds: 0}), {days: 1, seconds: -1});
		setStartDate(newStartDate);
		setEndDate(newEndDate);
	}

	// function mapReportRowData<T>(rowData: T[], map: (data: T[]) => HasKey[]): HasKey[] {
	// 	return map(rowData);
	// }

	function getTableRowData(rowInfos: RowInfo[], tabGroup?: string): any[] {
		let tableRowData: any[] = [];
		report.table.forEach((element: any) => {
			if (element.response?.data?.tillReportData.length > 0 && element.storeId != -1) {
				const storeRows = element.response.data.tillReportData.map((i: any) => ({ ...i, storeName: element.storeName, storeId: element.storeId }))
				const tables2 = rowInfos.map((i) => (
					{
						tabGroup: tabGroup,
						leafValue: i.leafValue.isField ? storeRows[0][i.leafValue.value] : i.leafValue.value,
						mainGroup: i.mainGroup && (i.mainGroup.isField ? storeRows[0][i.mainGroup.value] : i.mainGroup.value),
						subGroup: i.subGroup && (i.subGroup.isField ? storeRows[0][i.subGroup.value] : i.subGroup.value),
						subSubGroup: i.subSubGroup && (i.subSubGroup.isField ? storeRows[0][i.subSubGroup.value] : i.subSubGroup.value),
						subSubSubGroup: i.subSubSubGroup && (i.subSubSubGroup.isField ? storeRows[0][i.subSubSubGroup.value] : i.subSubSubGroup.value),
						...storeRows.reduce((period: any, curr: TillReportPeriodData) => {
							period[curr.tillPeriodKey] = i.key ? getProperValue(curr[i.key], i.isValueNegative) ?? 0 : null;
							return period;
						}, {})
					}));

				tableRowData.push(...tables2);
			}
		});

		console.log("table row data: ", tableRowData);

		return tableRowData;
	}

	function getPaymentBreakdownRowData(count: boolean): any[] {
		let paymentGroupNameMap = new Map<string, string>([
			["A", "Cash"],
			["B", "Card Payments"],
			["C", "Other"]
		]);
		let tableRowData: any[] = [];
		report.table.forEach((element) => {
			if (element.response?.data?.tillReportData.length > 0 && element.storeId != -1) {

				const storeRows = element.response.data.tillReportData.map((i: any) => ({ ...i, storeName: element.storeName, storeId: element.storeId }))
				const paymentData = element.response.data.tillReportData.flatMap((i: any) => {
					return i.paymentData.flatMap((pd: any) =>  {
						return { description: pd.description, paymentGroup: pd.paymentGroup }
					});
				}).filter((item: any, index: number, self: any) =>
					index === self.findIndex(
					t => t.description === item.description && t.paymentGroup === item.paymentGroup
				)).sort((a,b) => (a.paymentGroup < b.paymentGroup) ? -1 : 1); // Filter is filtering out duplicate (description, paymentGroup) pairs.

				const table = paymentData.map(pd => {
					return {
						tabGroup: undefined,
						leafValue: storeRows[0]["storeName"],
						mainGroup: "Total Payments",
						subGroup: paymentGroupNameMap.get(pd.paymentGroup) ?? "Unknown",
						subSubGroup: pd.description,
						subSubSubGroup: undefined,
						...storeRows.reduce((period: any, curr: TillReportPeriodData) => {
							period[curr.tillPeriodKey] = (count ? 
														curr.paymentData.find(d => pd.description === d.description && pd.paymentGroup === d.paymentGroup)?.paymentCount :
														curr.paymentData.find(d => pd.description === d.description && pd.paymentGroup === d.paymentGroup)?.paymentAmount) ?? 0;
							return period;
						}, {})
					}
				});

				tableRowData.push(...table);
			}
		});

		return tableRowData;
	}
	
	function getProperValue(value: any, isNegative?: boolean) {
		// Some logic to flip value to be negative if it needs to be, for things like "Less Petty Cash" etc, to help with aggregation
		if (typeof value === "number") {
			return value * (isNegative ? -1 : 1);
		} else {
			return value;
		}
	}

	function constructTillSummaryTable() {
		/** --- TILL SUMMARY REPORT --- */
		const tillSummaryCountRowData: RowInfo[] = [
			{ mainGroup: { value: "Total Customers Served" }, leafValue: { value: "storeName", isField: true }, key: "totalReceipts" },
			{ mainGroup: { value: "Total Sales" }, subGroup: { value: "OTC Sales" }, leafValue: { value: "storeName", isField: true } , key: "totalOTCSales_Count" },
			{ mainGroup: { value: "Total Sales" }, subGroup: { value: "Prescription Sales" }, leafValue: { value: "storeName", isField: true } , key: "prescriptionSales_Count" },
			{ mainGroup: { value: "Total Sales" }, subGroup: { value: "Prescription Sales (inc Government Contribution)" }, leafValue: { value: "storeName", isField: true }, key: null },
			{ mainGroup: { value: "Total Government Contribution" }, leafValue: { value: "storeName", isField: true } , key: "totalGovtContrib_Count" },
			{ mainGroup: { value: "Total Till Turnover" }, leafValue: { value: "storeName", isField: true } , key: null },
			{ mainGroup: { value: "Total Takings" }, leafValue: { value: "storeName", isField: true } , key: null },
			{ mainGroup: { value: "Gross Profit ($)" }, leafValue: { value: "storeName", isField: true } , key: null },
			{ mainGroup: { value: "Gross Profit (%)" }, leafValue: { value: "storeName", isField: true } , key: null },
		];

		const tillSummaryValueRowData: RowInfo[] = [
			{ mainGroup: { value: "Total Customers Served" }, leafValue: { value: "storeName", isField: true } , key: "totalReceipts" },
			{ mainGroup: { value: "Total Sales" }, subGroup: { value: "OTC Sales" }, leafValue: { value: "storeName", isField: true } , key: "totalOTCSales_Value" },
			{ mainGroup: { value: "Total Sales" }, subGroup: { value: "Prescription Sales" }, leafValue: { value: "storeName", isField: true } , key: "prescriptionSales_Value" },
			{ mainGroup: { value: "Total Sales" }, subGroup: { value: "Prescription Sales (inc Government Contribution)" }, leafValue: { value: "storeName", isField: true }, key: "prescriptionSales_Inc_Govt_Value" },
			{ mainGroup: { value: "Total Government Contribution" }, leafValue: { value: "storeName", isField: true } , key: "totalGovtContrib_Value" },
			{ mainGroup: { value: "Total Till Turnover" }, leafValue: { value: "storeName", isField: true } , key: "totalTillTurnover" },
			{ mainGroup: { value: "Total Takings" }, leafValue: { value: "storeName", isField: true } , key: "totalStoreTakings" },
			{ mainGroup: { value: "Gross Profit ($)" }, leafValue: { value: "storeName", isField: true } , key: "grossProfit" },
			{ mainGroup: { value: "Gross Profit (%)" }, leafValue: { value: "storeName", isField: true } , key: "grossProfitRate" },
		]

		/** --- GST SUMMARY REPORT --- */
		const gstSummaryValueRowData: RowInfo[] = [
			{ mainGroup: { value: "Total Non-Debtor Sales" }, subGroup: { value: "GST Free Sales" }, leafValue: { value: "storeName", isField: true }, key: "gSTFree_NonDebtor_Sales_Value"},
			{ mainGroup: { value: "Total Non-Debtor Sales" }, subGroup: { value: "GST Sales" }, leafValue: { value: "storeName", isField: true }, key: "gST_NonDebtor_Sales_Value"},
			{ mainGroup: { value: "Total Debtor Sales" }, subGroup: { value: "GST Free Sales" }, leafValue: { value: "storeName", isField: true }, key: "gSTFree_Debtor_Sales_Value"},
			{ mainGroup: { value: "Total Debtor Sales" }, subGroup: { value: "GST Sales" }, leafValue: { value: "storeName", isField: true }, key: "gST_Debtor_Sales_Value"},
			
			{ mainGroup: { value: "Total GST Free Sales" }, leafValue: { value: "storeName", isField: true }, key: "total_GST_Free_Sales_Value"},
			{ mainGroup: { value: "Total GST Sales" }, leafValue: { value: "storeName", isField: true }, key: "total_GST_Sales_Value"},
			{ mainGroup: { value: "Total GST Collected" }, leafValue: { value: "storeName", isField: true }, key: "total_GST_Collected_Value"},
			{ mainGroup: { value: "Total GST Paid in Petty Cash" }, leafValue: { value: "storeName", isField: true }, key: "pettyCash_GST_Value"},
		];
		
		const gstSummaryCountRowData: RowInfo[] = [
			{ mainGroup: { value: "Total Non-Debtor Sales" }, subGroup: { value: "GST Free Sales" }, leafValue: { value: "storeName", isField: true }, key: "gSTFree_NonDebtor_Sales_Count"},
			{ mainGroup: { value: "Total Non-Debtor Sales" }, subGroup: { value: "GST Sales" }, leafValue: { value: "storeName", isField: true }, key: "gST_NonDebtor_Sales_Count"},
			{ mainGroup: { value: "Total Debtor Sales" }, subGroup: { value: "GST Free Sales" }, leafValue: { value: "storeName", isField: true }, key: "gSTFree_Debtor_Sales_Count"},
			{ mainGroup: { value: "Total Debtor Sales" }, subGroup: { value: "GST Sales" }, leafValue: { value: "storeName", isField: true }, key: "gST_Debtor_Sales_Count"},
			
			{ mainGroup: { value: "Total GST Free Sales" }, leafValue: { value: "storeName", isField: true }, key: "total_GST_Free_Sales_Count"},
			{ mainGroup: { value: "Total GST Sales" }, leafValue: { value: "storeName", isField: true }, key: "total_GST_Sales_Count"},
			{ mainGroup: { value: "Total GST Collected" }, leafValue: { value: "storeName", isField: true }, key: null},
			{ mainGroup: { value: "Total GST Paid in Petty Cash" }, leafValue: { value: "storeName", isField: true }, key: "pettyCash_GST_Count"},
		];

		function createStoreNameLeaf(key: keyof any, isValueNegative: boolean = false): RowGroupLeaf<any> {
			return {
				group: "storeName",
				isField: true,
				key,
				isValueNegative
			};
		}
		
		function createNode(group: string, children: NonEmptyArray<RowGroupTree<any>>): RowGroupNode<any> {
			return {
				group: group,
				children: children
			}
		};

		const totalTakingsValueRowDataTree: RowGroupTree<any>[] = [
			createNode("Total Takings", [
				createNode("Total Till Turnover", [
					createNode("Total Gross OTC Sales", [
						createNode("Gross OTC Sales", [createStoreNameLeaf("grossOTCSales_NonDebtor_SalesValue")]),
						createNode("Less Discounts", [createStoreNameLeaf("grossOTCSales_NonDebtor_Discounts_Value", true)]),
						createNode("Plus Surcharges", [createStoreNameLeaf("grossOTCSales_NonDebtor_Surcharges_Value")]),
						createNode("Plus Payment Surcharges", [createStoreNameLeaf("grossOTCSales_NonDebtor_PaymentSurcharges_Value")])
					]),
					createNode("Total Gross OTC Debtor Charges", [
						createNode("Plus Gross OTC Debtor Charges", [createStoreNameLeaf("grossOTCSales_Debtor_SalesValue")]),
						createNode("Less Discounts", [createStoreNameLeaf("grossOTCSales_Debtor_Discounts_Value", true)]),
						createNode("Plus Surcharges", [createStoreNameLeaf("grossOTCSales_Debtor_Surcharges_Value")]),
						createNode("Plus Payment Surcharges", [createStoreNameLeaf("grossOTCSales_Debtor_PaymentSurcharges_Value")])
					]),
					createNode("Total Gross Prescription Sales", [
						createNode("Plus Gross Prescription Sales", [createStoreNameLeaf("grossScriptSales_NonDebtor_SalesValue")]),
						createNode("Less Discounts", [createStoreNameLeaf("grossScriptSales_NonDebtor_Discounts_Value", true)]),
						createNode("Plus Surcharges", [createStoreNameLeaf("grossScriptSales_NonDebtor_Surcharges_Value")]),
						createNode("Plus Payment Surcharges", [createStoreNameLeaf("grossScriptSales_NonDebtor_PaymentSurcharges_Value")]),
						createNode("Plus Government Contribution", [createStoreNameLeaf("grossScriptSales_NonDebtor_GovtContribution_Value")])
					]),
					createNode("Total Gross Prescription Debtor Charges", [
						createNode("Plus Gross Prescription Debtor Charges", [createStoreNameLeaf("grossScriptSales_Debtor_SalesValue")]),
						createNode("Less Discounts", [createStoreNameLeaf("grossScriptSales_Debtor_Discounts_Value", true)]),
						createNode("Plus Surcharges", [createStoreNameLeaf("grossScriptSales_Debtor_Surcharges_Value")]),
						createNode("Plus Payment Surcharges", [createStoreNameLeaf("grossScriptSales_Debtor_PaymentSurcharges_Value")]),
						createNode("Plus Government Contribution", [createStoreNameLeaf("grossScriptSales_Debtor_GovtContribution_Value")])
					])
				]),
				createNode("Plus Debtor Account Payments", [createStoreNameLeaf("debtorPayments_Value")]),
				createNode("Plus Layby Account Payments", [createStoreNameLeaf("laybyPayments_Value")]),
				createNode("Plus Purchased Vouchers", [createStoreNameLeaf("purchasedVouchers_Value")]),
				createNode("Less Debtor Charges", [createStoreNameLeaf("debtorCharges_Value", true)]),
				createNode("Less Layby Purchases", [createStoreNameLeaf("laybyPurchases_Value", true)]),
				createNode("Less Redeemed Vouchers", [createStoreNameLeaf("redeemedVouchers_Value", true)]),
				createNode("Less Redeemed Club Dollars", [createStoreNameLeaf("redeemedClubDollars_Value", true)]),
				createNode("Less Petty Cash", [createStoreNameLeaf("pettyCash_SalesValue", true)]),
				createNode("Less Total Government Contributions", [createStoreNameLeaf("less_Total_Govt_Contrib_Value", true)]),
				createNode("Plus Total Rounding", [createStoreNameLeaf("totalRounding_Value")])
			])
		]

		/** --- TOTAL TAKINGS BREAKDOWN REPORT --- */
		const totalTakingsValueRowData: RowInfo[] = [
			{ 
				mainGroup: { value: "Total Takings" }, 
				subGroup: { value: "Total Till Turnover" }, 
				subSubGroup: { value: "Total Gross OTC Sales"}, 
				subSubSubGroup: { value: "Gross OTC Sales" }, 
				leafValue: { value: "storeName", isField: true }, 
				key: "grossOTCSales_NonDebtor_SalesValue" 
			},
			{ 
				mainGroup: { value: "Total Takings" }, 
				subGroup: { value: "Total Till Turnover" }, 
				subSubGroup: { value: "Total Gross OTC Sales"}, 
				subSubSubGroup: { value: "Less Discounts" }, 
				leafValue: { value: "storeName", isField: true }, 
				key: "grossOTCSales_NonDebtor_Discounts_Value", 
				isValueNegative: true 
			},
			{ mainGroup: { value: "Total Takings" }, subGroup: { value: "Total Till Turnover" }, subSubGroup: { value: "Total Gross OTC Sales"}, subSubSubGroup: { value: "Plus Surcharges" }, leafValue: { value: "storeName", isField: true }, key: "grossOTCSales_NonDebtor_Surcharges_Value" },
			{ mainGroup: { value: "Total Takings" }, subGroup: { value: "Total Till Turnover" }, subSubGroup: { value: "Total Gross OTC Sales"}, subSubSubGroup: { value: "Plus Payment Surcharges" }, leafValue: { value: "storeName", isField: true }, key: "grossOTCSales_NonDebtor_PaymentSurcharges_Value" },

			{ mainGroup: { value: "Total Takings" }, subGroup: { value: "Total Till Turnover" }, subSubGroup: { value: "Total Gross OTC Debtor Charges"}, subSubSubGroup: { value: "Plus Gross OTC Debtor Charges" }, leafValue: { value: "storeName", isField: true }, key: "grossOTCSales_Debtor_SalesValue" },
			{ mainGroup: { value: "Total Takings" }, subGroup: { value: "Total Till Turnover" }, subSubGroup: { value: "Total Gross OTC Debtor Charges"}, subSubSubGroup: { value: "Less Discounts" }, leafValue: { value: "storeName", isField: true }, key: "grossOTCSales_Debtor_Discounts_Value", isValueNegative: true },
			{ mainGroup: { value: "Total Takings" }, subGroup: { value: "Total Till Turnover" }, subSubGroup: { value: "Total Gross OTC Debtor Charges"}, subSubSubGroup: { value: "Plus Surcharges" }, leafValue: { value: "storeName", isField: true }, key: "grossOTCSales_Debtor_Surcharges_Value" },
			{ mainGroup: { value: "Total Takings" }, subGroup: { value: "Total Till Turnover" }, subSubGroup: { value: "Total Gross OTC Debtor Charges"}, subSubSubGroup: { value: "Plus Payment Surcharges" }, leafValue: { value: "storeName", isField: true }, key: "grossOTCSales_Debtor_PaymentSurcharges_Value" },
			
			{ mainGroup: { value: "Total Takings" }, subGroup: { value: "Total Till Turnover" }, subSubGroup: { value: "Total Gross Prescription Sales"}, subSubSubGroup: { value: "Plus Gross Prescription Sales" }, leafValue: { value: "storeName", isField: true }, key: "grossScriptSales_NonDebtor_SalesValue" },
			{ mainGroup: { value: "Total Takings" }, subGroup: { value: "Total Till Turnover" }, subSubGroup: { value: "Total Gross Prescription Sales"}, subSubSubGroup: { value: "Less Discounts" }, leafValue: { value: "storeName", isField: true }, key: "grossScriptSales_NonDebtor_Discounts_Value", isValueNegative: true },
			{ mainGroup: { value: "Total Takings" }, subGroup: { value: "Total Till Turnover" }, subSubGroup: { value: "Total Gross Prescription Sales"}, subSubSubGroup: { value: "Plus Surcharges" }, leafValue: { value: "storeName", isField: true }, key: "grossScriptSales_NonDebtor_Surcharges_Value" },
			{ mainGroup: { value: "Total Takings" }, subGroup: { value: "Total Till Turnover" }, subSubGroup: { value: "Total Gross Prescription Sales"}, subSubSubGroup: { value: "Plus Payment Surcharges" }, leafValue: { value: "storeName", isField: true }, key: "grossScriptSales_NonDebtor_PaymentSurcharges_Value" },
			{ mainGroup: { value: "Total Takings" }, subGroup: { value: "Total Till Turnover" }, subSubGroup: { value: "Total Gross Prescription Sales"}, subSubSubGroup: { value: "Plus Government Contribution" }, leafValue: { value: "storeName", isField: true }, key: "grossScriptSales_NonDebtor_GovtContribution_Value" },
		
			{ mainGroup: { value: "Total Takings" }, subGroup: { value: "Total Till Turnover" }, subSubGroup: { value: "Total Gross Prescription Debtor Charges"}, subSubSubGroup: { value: "Plus Gross Prescription Debtor Charges" }, leafValue: { value: "storeName", isField: true }, key: "grossScriptSales_Debtor_SalesValue" },
			{ mainGroup: { value: "Total Takings" }, subGroup: { value: "Total Till Turnover" }, subSubGroup: { value: "Total Gross Prescription Debtor Charges"}, subSubSubGroup: { value: "Less Discounts" }, leafValue: { value: "storeName", isField: true }, key: "grossScriptSales_Debtor_Discounts_Value", isValueNegative: true },
			{ mainGroup: { value: "Total Takings" }, subGroup: { value: "Total Till Turnover" }, subSubGroup: { value: "Total Gross Prescription Debtor Charges"}, subSubSubGroup: { value: "Plus Surcharges" }, leafValue: { value: "storeName", isField: true }, key: "grossScriptSales_Debtor_Surcharges_Value" },
			{ mainGroup: { value: "Total Takings" }, subGroup: { value: "Total Till Turnover" }, subSubGroup: { value: "Total Gross Prescription Debtor Charges"}, subSubSubGroup: { value: "Plus Payment Surcharges" }, leafValue: { value: "storeName", isField: true }, key: "grossScriptSales_Debtor_PaymentSurcharges_Value" },
			{ mainGroup: { value: "Total Takings" }, subGroup: { value: "Total Till Turnover" }, subSubGroup: { value: "Total Gross Prescription Debtor Charges"}, subSubSubGroup: { value: "Plus Government Contribution" }, leafValue: { value: "storeName", isField: true }, key: "grossScriptSales_Debtor_GovtContribution_Value" },
			
			{ 
				mainGroup: { value: "Total Takings" }, 
				subGroup: { value: "Plus Debtor Account Payments"}, 
				leafValue: { value: "storeName", isField: true }, 
				key: "debtorPayments_Value" 
			},
			{ mainGroup: { value: "Total Takings" }, subGroup: { value: "Plus Layby Account Payments"}, leafValue: { value: "storeName", isField: true }, key: "laybyPayments_Value" },
			{ mainGroup: { value: "Total Takings" }, subGroup: { value: "Plus Purchased Vouchers"}, leafValue: { value: "storeName", isField: true }, key: "purchasedVouchers_Value" },
			{ mainGroup: { value: "Total Takings" }, subGroup: { value: "Less Debtor Charges"}, leafValue: { value: "storeName", isField: true }, key: "debtorCharges_Value", isValueNegative: true },
			{ mainGroup: { value: "Total Takings" }, subGroup: { value: "Less Layby Purchases"}, leafValue: { value: "storeName", isField: true }, key: "laybyPurchases_Value", isValueNegative: true },
			{ mainGroup: { value: "Total Takings" }, subGroup: { value: "Less Redeemed Vouchers"}, leafValue: { value: "storeName", isField: true }, key: "redeemedVouchers_Value", isValueNegative: true },
			{ mainGroup: { value: "Total Takings" }, subGroup: { value: "Less Redeemed Club Dollars"}, leafValue: { value: "storeName", isField: true }, key: "redeemedClubDollars_Value", isValueNegative: true },
			{ mainGroup: { value: "Total Takings" }, subGroup: { value: "Less Petty Cash"}, leafValue: { value: "storeName", isField: true }, key: "pettyCash_SalesValue", isValueNegative: true },
			{ mainGroup: { value: "Total Takings" }, subGroup: { value: "Less Total Government Contributions"}, leafValue: { value: "storeName", isField: true }, key: "less_Total_Govt_Contrib_Value", isValueNegative: true },
			{ mainGroup: { value: "Total Takings" }, subGroup: { value: "Plus Total Rounding"}, leafValue: { value: "storeName", isField: true }, key: "totalRounding_Value" },

		];

		const totalTakingsCountRowData: RowInfo[] = [
			{ mainGroup: { value: "Total Takings" }, subGroup: { value: "Total Till Turnover" }, subSubGroup: { value: "Total Gross OTC Sales"}, subSubSubGroup: { value: "Gross OTC Sales" }, leafValue: { value: "storeName", isField: true }, key: "grossOTCSales_NonDebtor_SalesCount" },
			{ mainGroup: { value: "Total Takings" }, subGroup: { value: "Total Till Turnover" }, subSubGroup: { value: "Total Gross OTC Sales"}, subSubSubGroup: { value: "Less Discounts" }, leafValue: { value: "storeName", isField: true }, key: "grossOTCSales_NonDebtor_Discounts_Count", isValueNegative: true },
			{ mainGroup: { value: "Total Takings" }, subGroup: { value: "Total Till Turnover" }, subSubGroup: { value: "Total Gross OTC Sales"}, subSubSubGroup: { value: "Plus Surcharges" }, leafValue: { value: "storeName", isField: true }, key: "grossOTCSales_NonDebtor_Surcharges_Count" },
			{ mainGroup: { value: "Total Takings" }, subGroup: { value: "Total Till Turnover" }, subSubGroup: { value: "Total Gross OTC Sales"}, subSubSubGroup: { value: "Plus Payment Surcharges" }, leafValue: { value: "storeName", isField: true }, key: "grossOTCSales_NonDebtor_PaymentSurcharges_Count" },

			{ mainGroup: { value: "Total Takings" }, subGroup: { value: "Total Till Turnover" }, subSubGroup: { value: "Total Gross OTC Debtor Charges"}, subSubSubGroup: { value: "Plus Gross OTC Debtor Charges" }, leafValue: { value: "storeName", isField: true }, key: "grossOTCSales_Debtor_SalesCount" },
			{ mainGroup: { value: "Total Takings" }, subGroup: { value: "Total Till Turnover" }, subSubGroup: { value: "Total Gross OTC Debtor Charges"}, subSubSubGroup: { value: "Less Discounts" }, leafValue: { value: "storeName", isField: true }, key: "grossOTCSales_Debtor_Discounts_Count", isValueNegative: true },
			{ mainGroup: { value: "Total Takings" }, subGroup: { value: "Total Till Turnover" }, subSubGroup: { value: "Total Gross OTC Debtor Charges"}, subSubSubGroup: { value: "Plus Surcharges" }, leafValue: { value: "storeName", isField: true }, key: "grossOTCSales_Debtor_Surcharges_Count" },
			{ mainGroup: { value: "Total Takings" }, subGroup: { value: "Total Till Turnover" }, subSubGroup: { value: "Total Gross OTC Debtor Charges"}, subSubSubGroup: { value: "Plus Payment Surcharges" }, leafValue: { value: "storeName", isField: true }, key: "grossOTCSales_Debtor_PaymentSurcharges_Count" },
			
			{ mainGroup: { value: "Total Takings" }, subGroup: { value: "Total Till Turnover" }, subSubGroup: { value: "Total Gross Prescription Sales"}, subSubSubGroup: { value: "Plus Gross Prescription Sales" }, leafValue: { value: "storeName", isField: true }, key: "grossScriptSales_NonDebtor_SalesCount" },
			{ mainGroup: { value: "Total Takings" }, subGroup: { value: "Total Till Turnover" }, subSubGroup: { value: "Total Gross Prescription Sales"}, subSubSubGroup: { value: "Less Discounts" }, leafValue: { value: "storeName", isField: true }, key: "grossScriptSales_NonDebtor_Discounts_Count", isValueNegative: true },
			{ mainGroup: { value: "Total Takings" }, subGroup: { value: "Total Till Turnover" }, subSubGroup: { value: "Total Gross Prescription Sales"}, subSubSubGroup: { value: "Plus Surcharges" }, leafValue: { value: "storeName", isField: true }, key: "grossScriptSales_NonDebtor_Surcharges_Count" },
			{ mainGroup: { value: "Total Takings" }, subGroup: { value: "Total Till Turnover" }, subSubGroup: { value: "Total Gross Prescription Sales"}, subSubSubGroup: { value: "Plus Payment Surcharges" }, leafValue: { value: "storeName", isField: true }, key: "grossScriptSales_NonDebtor_PaymentSurcharges_Count" },
			{ mainGroup: { value: "Total Takings" }, subGroup: { value: "Total Till Turnover" }, subSubGroup: { value: "Total Gross Prescription Sales"}, subSubSubGroup: { value: "Plus Government Contribution" }, leafValue: { value: "storeName", isField: true }, key: "grossScriptSales_NonDebtor_GovtContribution_Count" },
		
			{ mainGroup: { value: "Total Takings" }, subGroup: { value: "Total Till Turnover" }, subSubGroup: { value: "Total Gross Prescription Debtor Charges"}, subSubSubGroup: { value: "Plus Gross Prescription Debtor Charges" }, leafValue: { value: "storeName", isField: true }, key: "grossScriptSales_Debtor_SalesCount" },
			{ mainGroup: { value: "Total Takings" }, subGroup: { value: "Total Till Turnover" }, subSubGroup: { value: "Total Gross Prescription Debtor Charges"}, subSubSubGroup: { value: "Less Discounts" }, leafValue: { value: "storeName", isField: true }, key: "grossScriptSales_Debtor_Discounts_Count", isValueNegative: true },
			{ mainGroup: { value: "Total Takings" }, subGroup: { value: "Total Till Turnover" }, subSubGroup: { value: "Total Gross Prescription Debtor Charges"}, subSubSubGroup: { value: "Plus Surcharges" }, leafValue: { value: "storeName", isField: true }, key: "grossScriptSales_Debtor_Surcharges_Count" },
			{ mainGroup: { value: "Total Takings" }, subGroup: { value: "Total Till Turnover" }, subSubGroup: { value: "Total Gross Prescription Debtor Charges"}, subSubSubGroup: { value: "Plus Payment Surcharges" }, leafValue: { value: "storeName", isField: true }, key: "grossScriptSales_Debtor_PaymentSurcharges_Count" },
			{ mainGroup: { value: "Total Takings" }, subGroup: { value: "Total Till Turnover" }, subSubGroup: { value: "Total Gross Prescription Debtor Charges"}, subSubSubGroup: { value: "Plus Government Contribution" }, leafValue: { value: "storeName", isField: true }, key: "grossScriptSales_Debtor_GovtContribution_Count" },
			
			{ mainGroup: { value: "Total Takings" }, subGroup: { value: "Plus Debtor Account Payments"}, leafValue: { value: "storeName", isField: true }, key: "debtorPayments_Count" },
			{ mainGroup: { value: "Total Takings" }, subGroup: { value: "Plus Layby Account Payments"}, leafValue: { value: "storeName", isField: true }, key: "laybyPayments_Count" },
			{ mainGroup: { value: "Total Takings" }, subGroup: { value: "Plus Purchased Vouchers"}, leafValue: { value: "storeName", isField: true }, key: "purchasedVouchers_Count" },
			{ mainGroup: { value: "Total Takings" }, subGroup: { value: "Less Debtor Charges"}, leafValue: { value: "storeName", isField: true }, key: "debtorCharges_Count", isValueNegative: true },
			{ mainGroup: { value: "Total Takings" }, subGroup: { value: "Less Layby Purchases"}, leafValue: { value: "storeName", isField: true }, key: "laybyPurchases_Count", isValueNegative: true },
			{ mainGroup: { value: "Total Takings" }, subGroup: { value: "Less Redeemed Vouchers"}, leafValue: { value: "storeName", isField: true }, key: "redeemedVouchers_Count", isValueNegative: true },
			{ mainGroup: { value: "Total Takings" }, subGroup: { value: "Less Redeemed Club Dollars"}, leafValue: { value: "storeName", isField: true }, key: "redeemedClubDollars_Count", isValueNegative: true },
			{ mainGroup: { value: "Total Takings" }, subGroup: { value: "Less Petty Cash"}, leafValue: { value: "storeName", isField: true }, key: "pettyCash_SalesCount", isValueNegative: true },
			{ mainGroup: { value: "Total Takings" }, subGroup: { value: "Less Total Government Contributions"}, leafValue: { value: "storeName", isField: true }, key: "less_Total_Govt_Contrib_Count", isValueNegative: true },
			{ mainGroup: { value: "Total Takings" }, subGroup: { value: "Plus Total Rounding"}, leafValue: { value: "storeName", isField: true }, key: "totalRounding_Count" },
		];

		/** --- PAYMENT BREAKDOWN REPORT --- */
		setPaymentBreakdownValueTable(getPaymentBreakdownRowData(false));
		setPaymentBreakdownCountTable(getPaymentBreakdownRowData(true));

		/** --- STATISTICS SUMMARY REPORT --- */
		const statsSummaryValueRowData: RowInfo[] = [
			{ mainGroup: { value: "Avg. OTC Sales Per Customer" }, leafValue: { value: "storeName", isField: true }, key: "avg_OTCSales_PerCustomer_Value"},
			{ mainGroup: { value: "Avg. Prescription Sales Per Customer" }, leafValue: { value: "storeName", isField: true }, key: "avg_PrescriptionSales_PerCustomer_Value"},
			{ mainGroup: { value: "Total Discounts" }, leafValue: { value: "storeName", isField: true }, key: "totalDiscounts_Value"},
			{ mainGroup: { value: "Avg. Discounts Per Customer" }, leafValue: { value: "storeName", isField: true }, key: "avg_Discounts_PerCustomer_Value"},
			{ mainGroup: { value: "Total Surcharges" }, leafValue: { value: "storeName", isField: true }, key: "totalSurcharge_Value"},
			{ mainGroup: { value: "Avg. Surcharges Per Customer" }, leafValue: { value: "storeName", isField: true }, key: "avg_Surcharge_PerCustomer_Value"},
			{ mainGroup: { value: "Total Payment Surcharges" }, leafValue: { value: "storeName", isField: true }, key: "totalPaymentSurcharge_Value"},
			{ mainGroup: { value: "Avg. Payment Surcharges Per Customer" }, leafValue: { value: "storeName", isField: true }, key: "avg_PaymentSurcharge_PerCustomer_Value"},
			{ mainGroup: { value: "Refunds" }, leafValue: { value: "storeName", isField: true }, key: "refunds_Value"},
			{ mainGroup: { value: "Voided Receipts" }, leafValue: { value: "storeName", isField: true }, key: "voidedReceipts_Value"},
			{ mainGroup: { value: "Agency Sales" }, leafValue: { value: "storeName", isField: true }, key: "agencySales_Value"},
			{ mainGroup: { value: "Agency Sales Commission" }, leafValue: { value: "storeName", isField: true }, key: "agencySales_Commission_Value"},
			{ mainGroup: { value: "Saved Sales" }, leafValue: { value: "storeName", isField: true }, key: "savedSales_Value"},
			{ mainGroup: { value: "Open Drawer" }, leafValue: { value: "storeName", isField: true }, key: null},
		];

		const statsSummaryCountRowData: RowInfo[] = [
			{ mainGroup: { value: "Avg. OTC Sales Per Customer" }, leafValue: { value: "storeName", isField: true }, key: "avg_OTCSales_PerCustomer_Count"},
			{ mainGroup: { value: "Avg. Prescription Sales Per Customer" }, leafValue: { value: "storeName", isField: true }, key: "avg_PrescriptionSales_PerCustomer_Count"},
			{ mainGroup: { value: "Total Discounts" }, leafValue: { value: "storeName", isField: true }, key: "totalDiscounts_Count"},
			{ mainGroup: { value: "Avg. Discounts Per Customer" }, leafValue: { value: "storeName", isField: true }, key: "avg_Discounts_PerCustomer_Count"},
			{ mainGroup: { value: "Total Surcharges" }, leafValue: { value: "storeName", isField: true }, key: "totalSurcharge_Count"},
			{ mainGroup: { value: "Avg. Surcharges Per Customer" }, leafValue: { value: "storeName", isField: true }, key: "avg_Surcharge_PerCustomer_Count"},
			{ mainGroup: { value: "Total Payment Surcharges" }, leafValue: { value: "storeName", isField: true }, key: "totalPaymentSurcharge_Count"},
			{ mainGroup: { value: "Avg. Payment Surcharges Per Customer" }, leafValue: { value: "storeName", isField: true }, key: "avg_PaymentSurcharge_PerCustomer_Count"},
			{ mainGroup: { value: "Refunds" }, leafValue: { value: "storeName", isField: true }, key: "refunds_Count"},
			{ mainGroup: { value: "Voided Receipts" }, leafValue: { value: "storeName", isField: true }, key: "voidedReceipts_Count"},
			{ mainGroup: { value: "Agency Sales" }, leafValue: { value: "storeName", isField: true }, key: "agencySales_Count"},
			{ mainGroup: { value: "Agency Sales Commission" }, leafValue: { value: "storeName", isField: true }, key: "agencySales_Commission_Count"},
			{ mainGroup: { value: "Saved Sales" }, leafValue: { value: "storeName", isField: true }, key: "savedSales_Count"},
			{ mainGroup: { value: "Open Drawer" }, leafValue: { value: "storeName", isField: true }, key: "openDrawer_Count"},
		];

		let periodColumns: any[] = [];
		report.table.forEach(store => {
			const tillReportData = store.response?.data?.tillReportData;
			if (tillReportData === undefined) return [];

			periodColumns.push(...tillReportData.flatMap((period: TillReportPeriodData) => {
				return { desc: period.tillPeriodDescription, key: period.tillPeriodKey };
			}));
		});

		// If we have multiple stores with differing till periods (maybe some are missing for some stores, want to make sure we capture a superset of all periods).
		periodColumns = periodColumns.filter((v, i, a) => a.findIndex(b => b.key === v.key) === i).map((p: {desc: string, key: string}) => {
			return {
				column: p.key,
				headerName: p.desc !== "" ? p.desc : "All",
			}
		});
		
		setPeriodColCountDefs([...periodColumns.flatMap(p => { return { field: p.column, headerName: p.headerName, headerClass: "ag-right-aligned-header", aggFunc: (params) => {
			
			// If we're looking at any of the str's in the str Arr below, use pre-calculated value from
			// the report response "ALL" store. This is because the till report is annoying and some fields
			// don't aggregate in a normal way like sum or avg.
			const strArr: { str: string, key: keyof TillReportPeriodData}[] = 
			[
				{ str: "OTC Sales", key: "avg_OTCSales_PerCustomer_Count" }, 
				{ str: "Prescription Sales", key: "avg_PrescriptionSales_PerCustomer_Count" }, 
				{ str: "Discounts", key: "avg_Discounts_PerCustomer_Count" }, 
				{ str: "Surcharges", key: "avg_Surcharge_PerCustomer_Count" }, 
				{ str: "Payment Surcharges", key: "avg_PaymentSurcharge_PerCustomer_Count" }
			];
			
			const matchingIx = strArr.map((s) => `Avg. ${s.str} Per Customer`).findIndex((s, ix) => s === params.rowNode.key || s === params.rowNode.data?.mainGroup);
			
			if (matchingIx != -1) {
				const storeData = report.table.find(x => x.storeId == -1)?.response?.data;
				return storeData.tillReportData.find(trd => trd.tillPeriodKey === p.column)[strArr[matchingIx].key];
			}

			return customSum(params.values);
		}, 
		cellRendererSelector: (params) => {		
			const strArr = ["OTC Sales", "Prescription Sales", "Discounts", "Surcharges", "Payment Surcharges"];
				
			if (strArr.map(s => `Avg. ${s} Per Customer`).find(s => s === params.node.key || s === params.node.data?.mainGroup)) {
				return { component: 'decimalCellRenderer', params: { align: 'right' }};
			}

			return { component: 'integerCellRenderer', params: { align: "right" }}
		}}
		})]);
		setPeriodColValueDefs([...periodColumns.flatMap(p => { return { field: p.column, headerName: p.headerName, headerClass: "ag-right-aligned-header", aggFunc: params => {
			if (params.rowNode.data?.mainGroup === "Gross Profit (%)" || params.rowNode.key === "Gross Profit (%)") {
				const storeData = report.table.find(x => x.storeId == -1)?.response?.data;
				return storeData.tillReportData.find(trd => trd.tillPeriodKey === p.column).grossProfitRate;
			}

			// Really hacky, I know that Presc Incl Gov contrib is index 2 of the values array so I can manually exclude that from the aggregation
			if (params.rowNode.data?.mainGroup === "Total Sales" || params.rowNode.key === "Total Sales") {
				return customSum(params.values.filter((v, i, a) => i != 2));
			}

			const strArr: { str: string, key: keyof TillReportPeriodData}[] = 
			[
				{ str: "OTC Sales", key: "avg_OTCSales_PerCustomer_Value" }, 
				{ str: "Prescription Sales", key: "avg_PrescriptionSales_PerCustomer_Value" }, 
				{ str: "Discounts", key: "avg_Discounts_PerCustomer_Value" }, 
				{ str: "Surcharges", key: "avg_Surcharge_PerCustomer_Value" }, 
				{ str: "Payment Surcharges", key: "avg_PaymentSurcharge_PerCustomer_Value" }
			];
			
			const matchingIx = strArr.map((s) => `Avg. ${s.str} Per Customer`).findIndex((s, ix) => s === params.rowNode.key || s === params.rowNode.data?.mainGroup);
			
			if (matchingIx != -1) {
				const storeData = report.table.find(x => x.storeId == -1)?.response?.data;
				return storeData.tillReportData.find(trd => trd.tillPeriodKey === p.column)[strArr[matchingIx].key];
			}

			return customSum(params.values);
		}, cellRendererSelector: (params) => {
			if (params.node.data?.mainGroup === "Total Customers Served" || params.node.key === "Total Customers Served") {
				return { component: 'integerCellRenderer', params: { align: "right" } }; 
			} else if (params.node.data?.mainGroup === "Gross Profit (%)" || params.node.key === "Gross Profit (%)") {
				return { component: 'percentCellRenderer', params: { align: "right" } }
			}
			return { component: 'priceCellRenderer', params: { align: "right" } } 
		} } as ZColDef})]);

		setTillSummaryValueTable(getTableRowData(tillSummaryValueRowData));
		setTillSummaryCountTable(getTableRowData(tillSummaryCountRowData));

		setGstSummaryValueTable(getTableRowData(gstSummaryValueRowData));
		setGstSummaryCountTable(getTableRowData(gstSummaryCountRowData));
		
		setTotalTakingsValueTable(getTableRowData(totalTakingsValueRowData));
		setTotalTakingsCountTable(getTableRowData(totalTakingsCountRowData));

		setStatsSummaryValueTable(getTableRowData(statsSummaryValueRowData));
		setStatsSummaryCountTable(getTableRowData(statsSummaryCountRowData));

		setCompleteValueTable([...getTableRowData(tillSummaryValueRowData, "Till Summary"), ...getTableRowData(gstSummaryValueRowData, "Gst Summary"), ...getTableRowData(totalTakingsValueRowData, "Total Takings Breakdown"), ...getTableRowData(statsSummaryValueRowData, "Statistics Summary")])
		setCompleteCountTable([...getTableRowData(tillSummaryCountRowData, "Till Summary"), ...getTableRowData(gstSummaryCountRowData, "Gst Summary"), ...getTableRowData(totalTakingsCountRowData, "Total Takings Breakdown"), ...getTableRowData(statsSummaryCountRowData, "Statistics Summary")])
		setConstructing(false);
	}

	function exportTillReportClick() {
		dispatch(updateReportField({ key: 'error', value: "" }));
		
		if (selectedStores && selectedStores.length > 0) {
			setExporting(true);
			let functionFilters: TillReportFunction = {
				startDateTime: startDate ? formatISO(startDate) : null,
				endDateTime: endDate ? formatISO(endDate) : null,
				storeIds: selectedStores?.map(st => st.id) ?? [],
				intervals: selectedInterval
			}

			let sendPayload: ReportExportBody<TillReportFunction> = {
				exportFormat: 'EXCEL',
				function: functionFilters,
				reportName: report.name
			}

			dispatch(exportTillReport(sendPayload))
				.then((response) => {
					const xslxBlob = new Blob([response], { type: "application/xslx" });
					const url = window.URL.createObjectURL(xslxBlob);
					const tempLink = document.createElement("a");
					tempLink.href = url;
					tempLink.setAttribute(
					"download",
					"TillReport_"+ new Date().toLocaleDateString('en-AU', {
							day : 'numeric',
							month : 'short',
							year : 'numeric',
							hour : '2-digit',
							minute : '2-digit'
						}).split(' ').join('-').replace(',', '') +".xlsx"
					);
					document.body.appendChild(tempLink);
					tempLink.click();
					// Clean up the temporary elements and URL
					document.body.removeChild(tempLink);
					window.URL.revokeObjectURL(url);
				}).finally(() => {
					setExporting(false);
				});
		} else {
			dispatch(updateReportField({ key: 'error', value: "Store must be selected" }));
		}
	}

	function exportLocalTillReportClick() {
		if (completeTableRef.current !== null) {
			completeTableRef.current.ExportToExcel();
		}
	}

	const GenerateButton = 
		<div>
			<Button className="ms-1 cta-button"
				disabled={!generateButtonEnabled || generating || exporting || constructing}
				onClick={() => {
					setGenerating(true);
					setConstructing(true);
					dispatch(generateGroupTillReport({
						startDateTime: startDate ? formatISO(startDate) : null,
						endDateTime: endDate ? formatISO(endDate) : null,
						storeIds: selectedStores?.map(st => st.id) ?? [],
						intervals: selectedInterval
						})).then(() => {
							setGenerating(false);
						});
					}}>
				{generating ? <Spinner className="spinner-border-sm me-1" color="white" />: <></>}
				{generating ? <>Generating...</> : <>Generate</>}
			</Button>
		</div>;

	const GenerateButtonWithTooltip =
		<OverlayTrigger placement="top" overlay={
			<Tooltip id="tooltip-bottom" style={{position:"fixed"}}>
				{generateButtonTooltip}
			</Tooltip>}
		>
			{GenerateButton}
		</OverlayTrigger>;

	const ExportButton = 
		<div><Button className="ms-1 cta-button" variant="light" disabled={!exportButtonEnabled || generating || exporting || constructing} onClick={exportTillReportClick} >
		{exporting ? <><Spinner className="spinner-border-sm me-1" color="white" /></> : <></>}
		{exporting ? <>Exporting...</> : <>Export</>}
	</Button></div>;

	const ExportButtonWithTooltip = 
		<OverlayTrigger placement="top" overlay={
			<Tooltip id="tooltip-bottom" style={{position:"fixed"}}>
				{exportButtonTooltip}
			</Tooltip>}
		>
			{ExportButton}
		</OverlayTrigger>;

	return (
			<>
			<Title pageTitle={"Till Report"} />
			<Row>
				<Col xs={12}>
					<Card>
						<Card.Body>
							<Row className="mb-1">
								<Col xs={12}>
									<div className={`${exporting || generating ? "disabled-div" : ""}`}>
									<Row className="d-flex align-items-end mb-1">
										<Col sm={9} xl={3}>
											<ZMultiSelectDropdown
												hasSearch={true}
												canClear={true}
												label="Stores: "
												values={selectedStores}
												placeholder="Select stores"
												hasAllOption={true}
												options={stores.table}
												minMenuWidthPx={300}
												display={(s) => s.name}
												value={(s) => s.id}
												disabled={(s) => !s.connected}
												onSelectionChange={(e) => setSelectedStores(e)} />
										</Col>
										<Col sm={3} xl={1}>
											<Dropdown>
												<Dropdown.Toggle variant="light" className="d-flex align-items-center arrow-none select-dropdown-toggle" style={{ width: 'fit-content' }}>
													<i className="mdi mdi-eye" />
												</Dropdown.Toggle>
												<Dropdown.Menu className="p-0" style={{width: "fit-content"}}>
													<ZCheckboxButton className="p-1" type="radio" label="Show count" checked={showCount} onClick={() => setShowCount(true)} />
													<ZCheckboxButton className="p-1" type="radio" label="Show value" checked={!showCount} onClick={() => setShowCount(false)} />
												</Dropdown.Menu>
											</Dropdown>
										</Col>
										<Col sm={6} xl={2}>
											<ZSingleSelectDropdown
												label="Interval: "
												hasSearch={false}
												options={[TillPeriodGroupingIntervals.Daily, TillPeriodGroupingIntervals.Monthly, TillPeriodGroupingIntervals.Custom]}
												placeholder="Select interval"
												value={selectedInterval}
												optionKey={(o) => o}
												disabled={() => false}
												display={(o) => TillPeriodGroupingIntervals[o]}
												onSelectionChange={(o) => {
													setInterval(o);
												}}
											/>
										</Col>
										<Col sm={6} xl={2}>
											<ZSingleSelectDropdown
												label="Date Range: "
												hasSearch={false}
												options={dateRangeOptions}
												placeholder="Select date range"
												hasNoneOption={selectedInterval === TillPeriodGroupingIntervals.Custom}
												dropdownIsDisabled={selectedInterval === TillPeriodGroupingIntervals.Custom}
												noneOptionLabel="---"
												value={selectedDateRange}
												optionKey={(o) => o}
												disabled={() => false}
												display={(o) => o}
												onSelectionChange={(o) => setSelectedDateRange(o)}
											/>
										</Col>
										<Col sm={6} xl={2}>
											<ZDateTimePicker 
												canClear={false} 
												hasMonth={selectedInterval === TillPeriodGroupingIntervals.Daily || selectedInterval === TillPeriodGroupingIntervals.Custom} 
												hasDate={selectedDateRange === DateRange.Week || selectedInterval === TillPeriodGroupingIntervals.Custom} 
												maxDate={selectedInterval === TillPeriodGroupingIntervals.Custom ? endDate ?? undefined : undefined} 
												value={startDate} 
												label="Start Date" 
												hasTime={selectedInterval === TillPeriodGroupingIntervals.Custom} 
												error={startDateError}
												onError={(e) => setStartDateError(e)}
												onChanged={(d) => onStartDateChanged(d)}
											/>
											</Col>
										<Col sm={6} xl={2}>
											<ZDateTimePicker 
												canClear={false} 
												hasMonth={selectedInterval === TillPeriodGroupingIntervals.Daily || selectedInterval === TillPeriodGroupingIntervals.Custom} 
												hasDate={selectedDateRange === DateRange.Week || selectedInterval === TillPeriodGroupingIntervals.Custom} 
												disabled={selectedInterval !== TillPeriodGroupingIntervals.Custom} 
												minDate={selectedInterval === TillPeriodGroupingIntervals.Custom ? startDate ?? undefined : undefined} 
												value={endDate}
												label="End Date" 
												hasTime={selectedInterval === TillPeriodGroupingIntervals.Custom} 
												error={endDateError}
												onError={(e) => setEndDateError(e)}
												onChanged={(d) => selectedInterval === TillPeriodGroupingIntervals.Custom && setEndDate(d)} 
											/>
										</Col>
									</Row>
									<Row>
										<Col xs={12}>
											<Tabs
												className="ag-grid-tabs-container"
												defaultActiveKey="till-summary"
												id="uncontrolled-tab-example"
											>
												<Tab eventKey="till-summary" title="Till Summary">
													<AgGrid
														heightPx={500}
														rowData={showCount ? tillSummaryCountTable : tillSummaryValueTable}
														columnDefs={showCount ? [...tableColDefs, ...periodColCountDefs] : [...tableColDefs, ...periodColValueDefs]}
														pagination={"none"}
														loading={generating}
														handleSubmit={handleSubmit}
														search={false}
													/>
												</Tab>
												<Tab eventKey="gst-summary" title="GST Summary">
													<AgGrid
														heightPx={500}
														rowData={showCount ? gstSummaryCountTable : gstSummaryValueTable}
														columnDefs={showCount ? [...tableColDefs, ...periodColCountDefs] : [...tableColDefs, ...periodColValueDefs]}
														pagination={"none"}
														loading={generating}
														handleSubmit={handleSubmit}
														search={false}
													/>
												</Tab>
												<Tab eventKey="takings-breakdown" title="Total Takings Breakdown">
													<AgGrid
														heightPx={500}
														rowData={showCount ? totalTakingsCountTable : totalTakingsValueTable}
														columnDefs={showCount ? [...tableColDefs, ...periodColCountDefs] : [...tableColDefs, ...periodColValueDefs]}
														pagination={"none"}
														loading={generating} 
														handleSubmit={handleSubmit}
														search={false}
														isGroupOpenByDefault={(p) => p.field === "mainGroup"}
													/>
												</Tab>
												<Tab eventKey="payment-breakdown" title="Payment Breakdown">
													<AgGrid
														heightPx={500}
														rowData={showCount ? paymentBreakdownCountTable : paymentBreakdownValueTable}
														columnDefs={showCount ? [...tableColDefs, ...periodColCountDefs] : [...tableColDefs, ...periodColValueDefs]}
														pagination={"none"}
														loading={generating}
														handleSubmit={handleSubmit}
														search={false}
														isGroupOpenByDefault={(p) => p.field === "mainGroup"}
													/>
												</Tab>
												<Tab eventKey="stats-summary" title="Statistics Summary">
													<AgGrid
														heightPx={500}
														rowData={showCount ? statsSummaryCountTable : statsSummaryValueTable}
														columnDefs={showCount ? [...tableColDefs, ...periodColCountDefs] : [...tableColDefs, ...periodColValueDefs]}
														pagination={"none"}
														loading={generating}
														handleSubmit={handleSubmit}
														search={false}
													/>
												</Tab>
											</Tabs>
										</Col>
									</Row>
									</div>
									<Row className='mt-3'>
										<Col xs={3}>
											<Link to="/app/reports" className="btn text-muted d-none d-sm-inline-block btn-link fw-semibold">
												<i className="mdi mdi-arrow-left"></i> Back to Reports {' '}
											</Link>
										</Col>
										<Col xs={9} className="d-flex flex-reverse-row justify-content-end">
											<TimedMessage message="Please wait while we generate your report..." delay={30} enabled={generating} className="mb-0 py-1 px-2 ms-auto" />
											<TimedMessage message="Please wait while we export your report..." delay={30} enabled={exporting} className="mb-0 py-1 px-2 ms-auto" />
											{ exportButtonEnabled ? ExportButton : ExportButtonWithTooltip }
											{ generateButtonEnabled ? GenerateButton : GenerateButtonWithTooltip }
											{/* <Button className="ms-auto" onClick={exportLocalTillReportClick} >
												Export via AgGrid<br/> NOT FOR PROD
											</Button> */}
										</Col>
									</Row>
								</Col>
							</Row>
						</Card.Body>
					</Card>
				</Col>
			</Row>

			<div hidden={true}>
				<AgGrid
					ref={completeTableRef}
					rowData={showCount ? completeCountTable : completeValueTable}
					columnDefs={showCount ? [...tableColDefs, ...periodColCountDefs] : [...tableColDefs, ...periodColValueDefs]}
					pagination={"none"}
					loading={generating}
					handleSubmit={handleSubmit}
					search={false}
				/>
			</div>
		</>
	);
}